/**
 * Limit / actual display values for trading rule violations.
 * Outside-trading-window violations store times in violation_metadata (DB columns are numeric).
 */

const COUNT_RULES = new Set(['max_trades_per_day', 'consecutive_loss_cooldown']);
const CURRENCY_RULES = new Set([
  'max_loss_per_day',
  'max_loss_per_position',
  'profit_target_per_day',
  'daily_loss_exceeded',
  'position_loss_exceeded',
]);
/** Limit = previous price, Actual = new price */
const PRICE_MODIFICATION_RULES = new Set(['stop_loss_modified', 'profit_target_modified']);

export function normalizeViolationRuleType(ruleType) {
  if (ruleType === 'trading_window') return 'outside_trading_window';
  return ruleType;
}

function parseMeta(meta) {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return meta;
}

function firstFiniteNumber(...candidates) {
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const n = typeof c === 'number' ? c : parseFloat(c);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Previous (original) price for stop/target modification rows */
function getModificationOriginalPrice(violation) {
  const meta = parseMeta(violation?.violation_metadata);
  return firstFiniteNumber(
    violation?.threshold_value,
    meta.original_price,
    meta.original_stop,
    meta.original_target,
  );
}

/** New price for stop/target modification rows */
function getModificationNewPrice(violation) {
  const meta = parseMeta(violation?.violation_metadata);
  return firstFiniteNumber(
    violation?.actual_value,
    meta.new_price,
    meta.new_stop,
    meta.new_target,
  );
}

function formatWindowsFromMeta(meta) {
  if (meta.allowed_windows) return String(meta.allowed_windows);
  if (typeof meta.threshold_value === 'string' && meta.threshold_value.trim()) {
    return meta.threshold_value;
  }
  if (meta.window_start && meta.window_end) {
    const tz = meta.timezone || 'America/New_York';
    return `${meta.window_start} - ${meta.window_end} ${tz}`;
  }
  const windows = meta.trading_windows;
  if (Array.isArray(windows) && windows.length > 0) {
    const tz = meta.timezone || 'America/New_York';
    const parts = windows
      .map((w) => {
        const start = w.startTime || w.start_time;
        const end = w.endTime || w.end_time;
        return start && end ? `${start} - ${end}` : null;
      })
      .filter(Boolean);
    if (parts.length > 0) return `${parts.join(', ')} ${tz}`;
  }
  return null;
}

function formatTradeTimeFromMeta(meta) {
  if (typeof meta.actual_value === 'string' && meta.actual_value.trim()) {
    return meta.actual_value;
  }
  if (meta.execution_time_formatted) return String(meta.execution_time_formatted);
  if (meta.trade_time) {
    const tz = meta.timezone || '';
    return tz ? `${meta.trade_time} ${tz}` : String(meta.trade_time);
  }
  return null;
}

export function violationHasLimitActual(ruleType) {
  return true;
}

export function isPlanDeviationRuleType(ruleType) {
  const normalizedRuleType = normalizeViolationRuleType(ruleType);
  return PRICE_MODIFICATION_RULES.has(normalizedRuleType);
}

export function getViolationLimitValue(violation) {
  if (!violation) return null;

  const ruleType = normalizeViolationRuleType(violation.rule_type);

  if (PRICE_MODIFICATION_RULES.has(ruleType)) {
    return getModificationOriginalPrice(violation);
  }

  if (violation.threshold_value != null) return violation.threshold_value;

  if (ruleType === 'outside_trading_window') {
    return formatWindowsFromMeta(parseMeta(violation.violation_metadata));
  }

  if (ruleType === 'trade_without_stop_loss') {
    const meta = parseMeta(violation.violation_metadata);
    if (meta.threshold_value) return meta.threshold_value;
    return 'Stop required';
  }

  return null;
}

export function getViolationActualValue(violation) {
  if (!violation) return null;

  const ruleType = normalizeViolationRuleType(violation.rule_type);

  if (PRICE_MODIFICATION_RULES.has(ruleType)) {
    return getModificationNewPrice(violation);
  }

  if (violation.actual_value != null) return violation.actual_value;

  if (ruleType === 'outside_trading_window') {
    return formatTradeTimeFromMeta(parseMeta(violation.violation_metadata));
  }

  if (ruleType === 'trade_without_stop_loss') {
    const meta = parseMeta(violation.violation_metadata);
    if (meta.actual_value) return meta.actual_value;
    if (meta.stop_loss_added_after_open) return 'Stop Added';
    return 'No stop';
  }

  return null;
}

function formatPriceValue(value, formatCurrency) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return String(value);
  if (typeof formatCurrency === 'function') return formatCurrency(n);
  return n.toFixed(2);
}

export function formatViolationLimit(violation, formatCurrency) {
  const value = getViolationLimitValue(violation);
  if (value == null) return '—';

  const ruleType = normalizeViolationRuleType(violation?.rule_type);
  if (COUNT_RULES.has(ruleType)) return String(Math.round(Number(value)));
  if (PRICE_MODIFICATION_RULES.has(ruleType)) {
    return formatPriceValue(value, formatCurrency);
  }
  if (CURRENCY_RULES.has(ruleType) && typeof formatCurrency === 'function') {
    return formatCurrency(value);
  }
  return String(value);
}

export function formatViolationActual(violation, formatCurrency) {
  const value = getViolationActualValue(violation);
  if (value == null) return '—';

  const ruleType = normalizeViolationRuleType(violation?.rule_type);
  if (COUNT_RULES.has(ruleType)) return String(Math.round(Number(value)));
  if (PRICE_MODIFICATION_RULES.has(ruleType)) {
    return formatPriceValue(value, formatCurrency);
  }
  if (ruleType === 'profit_target_per_day' && typeof formatCurrency === 'function') {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return String(value);
    if (amount > 0) return `+${formatCurrency(amount)}`;
    if (amount < 0) return formatCurrency(amount);
    return formatCurrency(0);
  }
  if (CURRENCY_RULES.has(ruleType) && typeof formatCurrency === 'function') {
    return formatCurrency(-Math.abs(Number(value)));
  }
  return String(value);
}

/** True when Target Daily Profit is still at/above the target (vs hit earlier then fell below). */
export function isProfitTargetCurrentlyAbove(violation) {
  if (!violation) return false;
  const meta = parseMeta(violation.violation_metadata);
  if (typeof meta.currentlyAboveTarget === 'boolean') return meta.currentlyAboveTarget;
  const actual = firstFiniteNumber(violation.actual_value, meta.currentDailyPnL);
  const threshold = firstFiniteNumber(violation.threshold_value, meta.threshold_value);
  if (actual == null || threshold == null) return false;
  return actual >= threshold;
}

/**
 * Milestone exists (target was hit earlier) but day P&L is now under the target.
 * Advisory only — not a hard rule break.
 */
export function isProfitTargetGaveBack(violation) {
  if (!violation) return false;
  const ruleType = normalizeViolationRuleType(violation.rule_type);
  if (ruleType !== 'profit_target_per_day') return false;
  return !isProfitTargetCurrentlyAbove(violation);
}

/** Label for Target Daily Profit rows: success vs gave-back advisory. */
export function getProfitTargetViolationLabel(violation) {
  if (isProfitTargetGaveBack(violation)) {
    return 'Hit profit target — then fell below';
  }
  return 'Hit Daily Profit Target';
}

/** Actual column accent: green for positive outcomes (profit target, stop later added). */
export function getViolationActualColor(violation, colors) {
  const ruleType = normalizeViolationRuleType(violation?.rule_type);
  if (ruleType === 'profit_target_per_day') {
    if (isProfitTargetCurrentlyAbove(violation)) {
      return colors?.chartUp || colors?.success || '#22c55e';
    }
    // Hit earlier in the day, but current P&L is back under the target
    return colors?.warning || colors?.textMuted || '#f59e0b';
  }
  if (ruleType === 'trade_without_stop_loss') {
    const meta = parseMeta(violation?.violation_metadata);
    const actual = getViolationActualValue(violation);
    if (meta.stop_loss_added_after_open === true || actual === 'Stop Added') {
      return colors?.chartUp || colors?.success || '#22c55e';
    }
  }
  return colors?.chartDown || '#dc3545';
}

/**
 * Whether a violation row should count toward violation stats / charts.
 * profit_target_per_day is a milestone, not a broken rule.
 * trade_without_stop_loss with a stop later added is kept in the table but excluded from counts.
 */
export function countsTowardViolationStats(violation) {
  if (!violation?.rule_type) return false;
  const ruleType = normalizeViolationRuleType(violation.rule_type);
  if (ruleType === 'profit_target_per_day') return false;
  if (isPlanDeviationRuleType(ruleType)) return false;
  if (ruleType === 'trade_without_stop_loss') {
    const meta = parseMeta(violation.violation_metadata);
    const actual = getViolationActualValue(violation);
    if (meta.stop_loss_added_after_open === true || actual === 'Stop Added') {
      return false;
    }
  }
  return true;
}
